import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Dialog with the accessibility basics done properly: focus moves in on open,
 * returns to the trigger on close, Escape closes, and Tab is trapped inside.
 */
export default function Modal({ title, subtitle, onClose, children, footer, wide = false, hidden = false }) {
  const panelRef = useRef(null);
  const restoreRef = useRef(null);
  const hiddenRef = useRef(hidden);
  hiddenRef.current = hidden;

  useEffect(() => {
    restoreRef.current = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    /**
     * On a touch device, focusing a text field on open throws up the keyboard
     * and, on iOS, zooms the page in before the person has even read the dialog.
     * Focus the dialog itself there: screen readers still land in the right
     * place, and the keyboard appears only when they choose to type.
     */
    const coarse = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
    if (coarse) {
      panelRef.current?.focus();
    } else {
      panelRef.current
        ?.querySelector('input:not([type=hidden]), textarea, select, button:not([data-autofocus-skip])')
        ?.focus();
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (hiddenRef.current) return; // map-picking mode handles its own cancel
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || hiddenRef.current) return;
      const focusables = [
        ...panelRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input:not([type=hidden]), select, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      restoreRef.current?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="modal-backdrop"
      style={hidden ? { visibility: 'hidden', opacity: 0, pointerEvents: 'none' } : undefined}
      aria-hidden={hidden || undefined}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="modal"
        style={wide ? { maxWidth: 720 } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
      >
        <header className="modal-head">
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: 16 }}>{title}</h2>
            {subtitle && <p className="small muted" style={{ marginTop: 2 }}>{subtitle}</p>}
          </div>
          <div className="spacer" />
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Fermer" data-autofocus-skip>
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
